import Table from '@Common/Table';
import { useTestCaseContext } from '@Context/TestCaseContext';
import { GeneratedHistory } from '@Models/types';
import { MagnifyingGlass, SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { BounceLoader, MoonLoader } from 'react-spinners';
import { GeneratedHistoryColumns } from './GenColumns';
import colors from '@Common/assets/colors';

const Generated = () => {
  const {
    generatedHistory,
    fetchGeneratedHistory,
    setSelectedGenerated,
    setViewType,
    setPanelOpen,
    genHistoryLoading,
  } = useTestCaseContext();

  const [search, setSearch] = useState('');
  const [filteredSearch, setFilteredSearch] =
    useState<GeneratedHistory[]>(generatedHistory);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);

    setFilteredSearch(
      generatedHistory.filter((item) => {
        const requirements = JSON.parse(item.requirements);
        const requirementsMatch = requirements.some(
          (req: { reference_num: string; name: string }) =>
            req.reference_num.toLowerCase().includes(query.toLowerCase()) ||
            req.name.toLowerCase().includes(query.toLowerCase())
        );

        return requirementsMatch;
      })
    );
  };

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(generatedHistory);
  };

  const handleSelectGenerated = (history: GeneratedHistory) => {
    if (history.completion_status === 'completed') {
      setSelectedGenerated(history);
      setViewType('TestCases');
      setPanelOpen(true);
    }
  };

  useEffect(() => {
    if (generatedHistory.length === 0) {
      fetchGeneratedHistory();
    }
  }, []);

  useEffect(() => {
    setFilteredSearch(generatedHistory);
  }, [generatedHistory]);

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white">
      <span className="flex w-full flex-col items-start justify-between gap-2 rounded-t-md bg-primary-700 px-4 py-2 md:flex-row md:items-center md:gap-0">
        <span
          data-testid="test-generated-list-title"
          className="font-extrabold text-white"
        >
          Generated Test Cases
        </span>

        <div className="flex w-full flex-row items-center justify-between rounded-md border bg-white px-2 py-1 md:w-auto">
          <div className="flex items-center gap-x-2">
            <MagnifyingGlass size={20} weight="bold" />
            <input
              type="text"
              className="border-gray-200 font-inter text-sm focus:outline-none"
              placeholder="Search"
              value={search}
              onChange={handleSearch}
              disabled={genHistoryLoading}
            />
          </div>
          {genHistoryLoading ? (
            <BounceLoader size={20} color={colors.brandWarning[500]} />
          ) : (
            <X
              data-testid="test-generated-search-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          )}
        </div>
      </span>

      <div className="mx-2 mb-2 flex h-0 flex-grow flex-col">
        {!genHistoryLoading && generatedHistory.length === 0 && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label
              data-testid="test-no-generated-list-title"
              className="text-sm text-neutral-500"
            >
              No Generated Test Cases
            </label>
          </span>
        )}

        {genHistoryLoading ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
            <MoonLoader size={100} color="#071D49" />
            <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
              Generated Test Cases Loading
            </span>
          </span>
        ) : (
          <>
            {filteredSearch && filteredSearch.length > 0 && (
              <Table
                columns={GeneratedHistoryColumns}
                data={filteredSearch}
                rowClickHandler={handleSelectGenerated}
              />
            )}

            {generatedHistory.length > 0 && filteredSearch.length === 0 && (
              <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                <SquaresFour size={100} weight="bold" />
                <label className="text-xs text-neutral-500">
                  No History Record Found
                </label>
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Generated;
